import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { UploadCloud, X } from 'lucide-react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { auth, storage } from '../utils/firebase';
import { collection, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import Cropper from 'react-easy-crop';

const ActivityForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const [activity, setActivity] = useState({
    title: '',
    date: '',
    location: '',
    content: '',
    
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const ASPECT_RATIO: '16:9' = '16:9';
  const MAX_DIMENSION = 1600;
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgressPercent, setUploadProgressPercent] = useState<number | null>(null);
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);
  const [existingImagePath, setExistingImagePath] = useState<string | null>(null);
  const [sourceImageDims, setSourceImageDims] = useState<{ width: number; height: number } | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles[0]) {
      const file = acceptedFiles[0];
      const maxBytes = 10 * 1024 * 1024; // 10MB
      if (file.size > maxBytes) {
        alert('画像サイズは10MB以下にしてください。');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: false,
  });

  // authentication check is handled in submit; no subscription needed

  useEffect(() => {
    if (isEditing && id) {
      (async () => {
        try {
          const docRef = doc(db, 'activities', id);
          const snap = await getDoc(docRef);
          if (snap.exists()) {
            const data = snap.data() as any;
            setActivity({
              title: data.title || '',
              date: data.date || '',
              location: data.location || '',
              content: data.content || '',
            });
            // Prefer explicit storage path when present; otherwise allow absolute URL
            const pathOrUrl: string = data.imagePath || data.imageUrl || '';
            if (pathOrUrl) {
              try {
                const url = pathOrUrl.startsWith('http')
                  ? pathOrUrl
                  : await getDownloadURL(ref(storage, pathOrUrl));
                setImagePreview(url);
                // Keep existingImagePath only when we have a storage path
                setExistingImagePath(data.imagePath || (pathOrUrl.startsWith('http') ? null : pathOrUrl));
              } catch (_) {
                // ignore preview error
              }
            }
          }
        } catch (err) {
          console.error('Error loading activity for edit: ', err);
        }
      })();
    }
    return () => {
      if (imagePreview) {
        try { URL.revokeObjectURL(imagePreview); } catch (_) { /* noop */ }
      }
    };
  }, [id, isEditing]);

  // Measure source image dimensions whenever preview changes
  useEffect(() => {
    (async () => {
      try {
        if (!imagePreview) {
          setSourceImageDims(null);
          return;
        }
        const img = await loadImageElement(imagePreview);
        setSourceImageDims({ width: img.naturalWidth, height: img.naturalHeight });
      } catch (_) {
        setSourceImageDims(null);
      }
    })();
  }, [imagePreview]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setActivity(prev => ({ ...prev, [name]: value }));
  };

  const withTimeout = async <T,>(promise: Promise<T>, ms: number): Promise<T> => {
    return new Promise<T>((resolve, reject) => {
      const id = setTimeout(() => reject(new Error('timeout')), ms);
      promise
        .then((res) => {
          clearTimeout(id);
          resolve(res);
        })
        .catch((err) => {
          clearTimeout(id);
          reject(err);
        });
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitErrorMessage(null);
    if (!navigator.onLine) {
      setSubmitErrorMessage('オフラインのためアップロードできません。ネットワーク接続をご確認ください。');
      return;
    }
    if (!auth.currentUser) {
      alert('この操作にはログインが必要です。ログインしてください。');
      navigate('/admin');
      return;
    }
    try {
      // Refresh ID token to avoid stale/expired sessions
      await auth.currentUser.getIdToken(true);
    } catch (_) {
      setSubmitErrorMessage('認証情報の更新に失敗しました。ログインし直してからお試しください。');
      return;
    }

    if (!activity.title || !activity.date || !activity.content) {
      alert('タイトル、日付、詳細内容は必須です。');
      return;
    }
    setIsSubmitting(true);

    // Use existing path by default; override if an image (new or existing) needs processing
    let imagePath = existingImagePath || '';
    const shouldProcess = Boolean(imagePreview && croppedAreaPixels);
    if (shouldProcess) {
      // Build a local object URL for the source image to avoid CORS issues when editing existing images
      let sourceUrl: string | null = null;
      try {
        if (imageFile) {
          sourceUrl = URL.createObjectURL(imageFile);
        } else if (imagePreview) {
          const res = await fetch(imagePreview);
          const blob = await res.blob();
          sourceUrl = URL.createObjectURL(blob);
        }
        if (!sourceUrl) throw new Error('source image unavailable');

        const processedBlob = await exportCroppedImage(sourceUrl, croppedAreaPixels, ASPECT_RATIO, MAX_DIMENSION);
        const baseName = imageFile ? sanitizeFilename(imageFile.name).replace(/\.[^.]+$/, '') : 'edited';
        const newImagePath = `activity_images/${Date.now()}_${baseName}.jpg`;
        const storageRef = ref(storage, newImagePath);
        setUploadProgressPercent(0);
        await withTimeout(new Promise<void>((resolve, reject) => {
          const task = uploadBytesResumable(storageRef, processedBlob, { contentType: 'image/jpeg' });
          task.on(
            'state_changed',
            (snapshot) => {
              const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
              setUploadProgressPercent(percent);
            },
            (error) => reject(error),
            () => resolve()
          );
        }), 30000);
        // Save path; public URL will be resolved below
        imagePath = newImagePath;
      } catch (error: any) {
        console.error('Error uploading image: ', error);
        if (error?.message === 'timeout') {
          setSubmitErrorMessage('画像のアップロードがタイムアウトしました。通信環境をご確認のうえ、再度お試しください。');
        } else {
          setSubmitErrorMessage(`画像のアップロードに失敗しました。${error?.code || ''}`);
        }
        setIsSubmitting(false);
        setUploadProgressPercent(null);
        return;
      } finally {
        if (imageFile && sourceUrl) URL.revokeObjectURL(sourceUrl);
      }
    }

    try {
      // Resolve public URL for storage path when available
      let publicUrl = '';
      if (imagePath) {
        try {
          publicUrl = await getDownloadURL(ref(storage, imagePath));
        } catch (_) {
          // leave empty on failure; UI will still work
        }
      }

      const payload = {
        ...activity,
        imageUrl: publicUrl || '',
        imagePath: imagePath || '',
      } as const;
      if (isEditing && id) {
        await withTimeout(updateDoc(doc(db, 'activities', id), payload), 30000);
        alert('活動記録を更新しました！');
      } else {
        await withTimeout(addDoc(collection(db, 'activities'), payload), 30000);
        alert('活動記録を保存しました！');
      }
      navigate('/admin/dashboard');
    } catch (error: any) {
      console.error('Error adding document: ', error);
      if (error?.message === 'timeout') {
        setSubmitErrorMessage('保存処理がタイムアウトしました。通信環境をご確認のうえ、再度お試しください。');
      } else {
        setSubmitErrorMessage(`データの保存に失敗しました。${error?.code || ''}`);
      }
    } finally {
      setIsSubmitting(false);
      setUploadProgressPercent(null);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    if (imagePreview) {
      try { URL.revokeObjectURL(imagePreview); } catch (_) { /* noop */ }
      setImagePreview(null);
    }
    setExistingImagePath(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const onCropComplete = (_: any, croppedPixels: { x: number; y: number; width: number; height: number }) => {
    setCroppedAreaPixels(croppedPixels);
  };

  function sanitizeFilename(name: string): string {
    return name.replace(/[^a-zA-Z0-9._-]/g, '_');
  }

  async function exportCroppedImage(
    imageSrc: string,
    area: { x: number; y: number; width: number; height: number } | null,
    aspect: '16:9' | '4:3' | '1:1' | 'original',
    maxDim: number
  ): Promise<Blob> {
    const img = await loadImageElement(imageSrc);
    // if no crop area (unexpected), use full image
    const crop = area || { x: 0, y: 0, width: img.naturalWidth, height: img.naturalHeight };

    // If aspect is fixed, adjust crop to exact aspect while keeping center
    let targetAspect: number | null = null;
    if (aspect === '16:9') targetAspect = 16 / 9; else if (aspect === '4:3') targetAspect = 4 / 3; else if (aspect === '1:1') targetAspect = 1; else targetAspect = null;
    let cw = crop.width;
    let ch = crop.height;
    if (targetAspect) {
      const cur = cw / ch;
      if (cur > targetAspect) {
        cw = Math.round(ch * targetAspect);
      } else {
        ch = Math.round(cw / targetAspect);
      }
    }

    // Clamp inside source
    let sx = Math.max(0, Math.min(crop.x, img.naturalWidth - cw));
    let sy = Math.max(0, Math.min(crop.y, img.naturalHeight - ch));

    // Resize output with constraints: at least MIN_LONG_EDGE and at most maxDim on the long edge
    const MIN_LONG_EDGE = 1200; // ensure SNS og:image is big enough (>= 200x200; recommended ~1200px)
    const currentLong = Math.max(cw, ch);
    const desiredLong = Math.min(Math.max(currentLong, MIN_LONG_EDGE), maxDim);
    const scale = desiredLong / currentLong;
    const outW = Math.max(1, Math.round(cw * scale));
    const outH = Math.max(1, Math.round(ch * scale));

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not supported');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, sx, sy, cw, ch, 0, 0, outW, outH);

    const JPEG_QUALITY = 0.8;
    const blob: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Failed to create blob'))), 'image/jpeg', JPEG_QUALITY);
    });
    return blob;
  }

  function loadImageElement(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
      // Use anonymous CORS to avoid tainting canvas when editing remote images
      img.crossOrigin = 'anonymous';
    });
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8" style={{ paddingTop: '80px' }}>
      <div className="max-w-4xl mx-auto bg-white p-6 rounded-lg shadow-md">
        <h1 className="text-2xl font-bold mb-6">{isEditing ? '活動記録の編集' : '活動記録の新規作成'}</h1>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">アイキャッチ画像</label>
            {imagePreview ? (
              <div className="relative">
                <div className="relative w-full rounded-lg overflow-hidden bg-black/5" style={{ height: 400 }}>
                  <Cropper
                    image={imagePreview}
                    crop={crop}
                    zoom={zoom}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                    aspect={16 / 9}
                    showGrid={true}
                  />
                </div>
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition-colors"
                >
                  <X size={18} />
                </button>
                <p className="mt-2 text-xs text-gray-600">ドラッグで位置を調整、ホイールや下のスライダーで拡大縮小できます。白い矩形が切り取り範囲です（比率: 16:9、最大長辺: 1600px 固定）。</p>
                <div className="mt-3">
                  <label className="block text-xs text-gray-600 mb-1">ズーム</label>
                  <input type="range" min={1} max={4} step={0.01} value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))} className="w-full" />
                </div>
              </div>
            ) : (
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
                  isDragActive ? 'border-pink-500 bg-pink-50' : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input {...getInputProps()} />
                <UploadCloud className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-600">
                  ここに画像をドラッグ＆ドロップするか、クリックしてファイルを選択
                </p>
                <p className="text-xs text-gray-500">PNG, JPG, WebP up to 10MB</p>
              </div>
            )}
          </div>
          {sourceImageDims && (
            <p className="mt-2 text-xs text-gray-600">
              元画像の解像度: {sourceImageDims.width}×{sourceImageDims.height}px。推奨: 長辺1200px以上（例: 1200×630）。{' '}
              {(sourceImageDims.width < 200 || sourceImageDims.height < 200) ? (
                <span className="text-red-600 font-semibold">この画像はSNSの画像プレビューの最小要件(200×200)未満です。差し替えを推奨します。</span>
              ) : (Math.max(sourceImageDims.width, sourceImageDims.height) < 1200 ? (
                <span className="text-amber-600">推奨より小さいため、SNSの画像プレビューでぼやける可能性があります。</span>
              ) : null)}
            </p>
          )}
          <div className="mb-4">
            <label className="block text-gray-700">タイトル</label>
            <input type="text" name="title" value={activity.title} onChange={handleChange} className="w-full p-2 border rounded" required />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700">日付</label>
            <input type="date" name="date" value={activity.date} onChange={handleChange} className="w-full p-2 border rounded" required />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700">場所</label>
            <input type="text" name="location" value={activity.location} onChange={handleChange} className="w-full p-2 border rounded" />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700">詳細内容</label>
            <textarea name="content" value={activity.content} onChange={handleChange} className="w-full p-2 border rounded" rows={10} required />
          </div>
          
          {uploadProgressPercent !== null && (
            <div className="mb-4">
              <div className="h-2 bg-gray-200 rounded">
                <div className="h-2 bg-pink-500 rounded" style={{ width: `${uploadProgressPercent}%` }}></div>
              </div>
              <p className="text-sm text-gray-600 mt-1">アップロード中... {uploadProgressPercent}%</p>
            </div>
          )}

          {submitErrorMessage && (
            <div className="mb-4 p-3 rounded bg-red-50 text-red-700 text-sm">{submitErrorMessage}</div>
          )}

          <div className="flex justify-end">
            <button type="button" onClick={() => navigate('/admin/dashboard')} className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded mr-2" disabled={isSubmitting}>
              キャンセル
            </button>
            <button type="submit" className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center justify-center min-w-[120px]" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                  </svg>
                  保存中...
                </>
              ) : (isEditing ? '更新' : '作成')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ActivityForm;
